import { Matrix4, Vector2, Vector3, Web3DViewer } from '@technology/web3d';
import { TileMapModel, TileMapPlugin } from '@technology/web3d-plugin-tilemap';
import { SceneProjection } from '@technology/web3d-plugin-tilemap/dist/esnext/common';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  Setter,
} from 'solid-js';
import { SingleCapabilityProviderBase } from '../base/single-provider-base';
import {
  convertBoundsToWebMercatorUsingCsib,
  convertTileMapOptionsToTileMapSettings,
  getProjectionOptions,
  getTileCoordsFromLatLon,
} from './tile-map-provider.helpers';
import {
  TileMapOptions,
  TileMapProviderProps,
} from './tile-map-provider.types';
import {
  lonLatToWebMercatorMeters,
  isLonLatCoordinate,
  Latitude,
  Longitude,
  detectObjectArrayChanges,
} from '@trimble/utilities';
import { getLogger } from '@trimble/logging';
import { defaultMercatorProjection } from './mercator-projection';

/**
 * Represents a provider for managing tile map operations within a Web3DViewer instance.
 * This class handles map initialization, active model changes, viewer positioning,
 * and scene projections for 2D navigation and rendering purposes.
 */
export class TileMapCapabilityProvider extends SingleCapabilityProviderBase<
  TileMapOptions,
  'modelId'
> {
  public idKey = 'modelId' as const;

  // eslint-disable-next-line no-magic-numbers
  private readonly DEFAULT_CAMERA_HEIGHT = 1000;

  private projectionOperationPromise: Promise<void> = Promise.resolve();

  // Promise that resolves when the first model is loaded
  private readonly modelLoadedPromise: Promise<void>;
  private resolveModelLoaded!: () => void;
  private readonly tglInitializedPromise: Promise<void>;
  private resolveTglInitialized!: () => void;
  private readonly sceneProjectionConfiguredPromise: Promise<void>;
  private resolveSceneProjectionConfiguredPromise!: () => void;

  public static readonly defaultMercatorProjection = defaultMercatorProjection;

  private sceneProjection?: SceneProjection;

  // Set the elevation value to move the map models to a specific elevation
  public readonly setElevation: Setter<number | undefined>;
  private readonly elevation: Accessor<number | undefined>;

  constructor(
    viewer: Web3DViewer,
    private readonly props: Accessor<TileMapProviderProps | undefined>
  ) {
    super(viewer);

    // Initialize elevation signal
    const [elevation, setElevation] = createSignal<number | undefined>();
    this.elevation = elevation;
    this.setElevation = setElevation;

    // Initialize the model loaded promise
    this.modelLoadedPromise = new Promise<void>((resolve) => {
      this.resolveModelLoaded = resolve;
    });

    // Initialize the TGL initialized promise
    this.tglInitializedPromise = new Promise<void>((resolve) => {
      this.resolveTglInitialized = resolve;
    });

    this.sceneProjectionConfiguredPromise = new Promise<void>((resolve) => {
      this.resolveSceneProjectionConfiguredPromise = resolve;
    });

    // Handle scene projection change and model load.
    createEffect(
      on([this.props, this.items], async ([props, models], prevValues) => {
        if (!props) {
          return;
        }

        // Wait for any previous execution to complete and queue this execution
        this.projectionOperationPromise = this.projectionOperationPromise
          .then(async () => {
            const [prevProps, prevModels] = prevValues ?? [
              undefined,
              undefined,
            ];

            // Handle projection changes
            const projectionChanged =
              JSON.stringify(props.projection) !==
              JSON.stringify(prevProps?.projection);

            if (projectionChanged) {
              const projectionOptions = await getProjectionOptions(
                props.projection
              );
              this.sceneProjection = projectionOptions;
              // When changing scene projection we need to unload and reload all
              // models on map due to internal implementation of web3d.
              this.viewer
                .getModels(TileMapModel)
                .forEach(
                  (m) =>
                    void this.viewer.unload(m.modelId, { fitToView: false })
                );

              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- web3d does not expose a public API for setSceneProjection
              (this.tileMapPlugin as any).setSceneProjection(projectionOptions);
              this.resolveSceneProjectionConfiguredPromise();
              if (
                projectionOptions.tglDef &&
                'geoDataFiles' in props.projection
              ) {
                try {
                  await this.tileMapPlugin.initTglTransforms(
                    props.projection.geoDataFiles
                  );
                  this.resolveTglInitialized();
                } catch (error) {
                  console.error('Error initializing TGL transforms:', error);
                }
              }
            }

            // With existing public methods you can't remove single item,
            // so we don't handle removed items here.
            const { newItems, unChangedItems } = detectObjectArrayChanges(
              prevModels,
              models,
              'modelId'
            );

            // Process models that need loading or updating
            const modelsToProcess = projectionChanged
              ? [...newItems, ...unChangedItems]
              : newItems;

            modelsToProcess.forEach((tileMapOptions) => {
              const tileMapSettings =
                convertTileMapOptionsToTileMapSettings(tileMapOptions);

              const viewerModel = this.viewer.getModel(
                tileMapSettings.modelId,
                TileMapModel
              );

              // Configure root coordinate mode when not using loadLimitBounds
              if (!('loadLimitBounds' in props)) {
                const tileCoordinate = isLonLatCoordinate(props.boundingCenter)
                  ? getTileCoordsFromLatLon(
                      props.boundingCenter.lat,
                      props.boundingCenter.lon,
                      props.rootZoomLevel
                    )
                  : this.tileMapPlugin.getTileCoordinate(
                      props.boundingCenter,
                      props.rootZoomLevel
                    );

                tileMapSettings.options.rootCoordinate = new Vector2(
                  tileCoordinate.x,
                  tileCoordinate.y
                );
                tileMapSettings.options.rootZoomLevel = props.rootZoomLevel;
              }

              const isVisible = tileMapOptions.visible ?? false;

              if (!viewerModel) {
                const tileMapPlugin = this.viewer.plugins.get(TileMapPlugin);
                if (!tileMapPlugin) {
                  throw new Error(
                    'TileMapPlugin is not available on the viewer'
                  );
                }
                const loadedModel = tileMapPlugin.load(tileMapSettings);

                const transform = elevationTranslation();
                if (transform) {
                  // Translate the loaded map model's elevation to the set Elevation
                  loadedModel.transform(transform);
                }

                loadedModel.visible = isVisible;

                // Resolve the promise when the first model is loaded
                this.resolveModelLoaded();
              } else {
                if (viewerModel.visible !== isVisible) {
                  viewerModel.visible = isVisible;
                }
              }
            });
          })
          .catch((error: unknown) => {
            console.error('Error in projection effect:', error);
          });

        // Wait for the operation to complete
        await this.projectionOperationPromise;
      })
    );

    const elevationTranslation = createMemo(() => {
      // Make a translation matrix to move the map model to the set Elevation
      const elevation = this.elevation();
      return elevation !== undefined
        ? new Matrix4().makeTranslation(0, 0, elevation)
        : undefined;
    });

    // Show/hide Base maps.
    createEffect(() => {
      const currModelId = this.selectedItemId();
      this.items()
        .filter((tileMapOption) => tileMapOption.tileMapType === 'base')
        .forEach((tileMapOption) => {
          const isVisible = tileMapOption.modelId === currModelId;
          tileMapOption.visible = isVisible;

          const mapModel = this.viewer.getModel(
            tileMapOption.modelId,
            TileMapModel
          );
          if (!mapModel) {
            return;
          }
          mapModel.visible = isVisible;
        });
    });

    createEffect(() => {
      // If elevation changes, translate the map models to the new elevation
      const transform = elevationTranslation();
      if (transform) {
        this.viewer.getModels(TileMapModel).map((m) => {
          m.transform(transform);
        });
      }
    });
  }

  /**
   * Sets the visibility of a specific overlay model by its model ID.
   * Base models visibility is controlled by setSelectedItemId.
   * @param modelId The ID of the overlay model to set visibility for.
   * @param visible Whether the model should be visible.
   */
  public setOverlayModelVisibility(modelId: string, visible: boolean): void {
    const tileMapOption = this.getById(modelId);

    if (tileMapOption) {
      if (tileMapOption.tileMapType !== 'overlay') {
        console.warn(
          'Visibility of base model is controlled by setSelectedItemId, not setOverlayModelVisibility'
        );
        return;
      }

      tileMapOption.visible = visible;

      const mapModel = this.viewer.getModel(modelId, TileMapModel);
      if (mapModel) {
        mapModel.visible = visible;
      }
    }
  }

  public async addMapModel(tileOptions: TileMapOptions): Promise<void> {
    if (!tileOptions.baseUrl) return; // Don't create a model for the "hidden" map
    const projectionOptions = await getProjectionOptions(
      tileOptions.projection
    );

    //If the model id already exists, we need to remove it and add it back with the new options
    const tileMapOption = this.getById(tileOptions.modelId);

    if (tileMapOption) {
      const mapModel = this.viewer.getModel(tileOptions.modelId, TileMapModel);
      if (mapModel) {
        mapModel.visible = false;
        await this.viewer.unload(mapModel.modelId, { fitToView: false });
      }
      this.removeItem(tileMapOption);
    }

    const props = this.props();

    // If using CSIB projection and loadLimitBounds provided, convert from source to Web Mercator
    let loadLimitBounds: [Vector2, Vector2] | undefined;
    if (props && 'loadLimitBounds' in props) {
      loadLimitBounds = props.loadLimitBounds;
      if ('csib' in props.projection && loadLimitBounds) {
        loadLimitBounds = await convertBoundsToWebMercatorUsingCsib(
          loadLimitBounds,
          props.projection.csib,
          props.projection.geoDataFiles
        );
      }
    }

    const optionsWithDefaults: TileMapOptions = {
      ...tileOptions,
      projection: {
        projectionOptions: {
          ...TileMapCapabilityProvider.defaultMercatorProjection,
          ...projectionOptions,
          loadLimitBounds:
            loadLimitBounds ??
            TileMapCapabilityProvider.defaultMercatorProjection.loadLimitBounds,
        },
      },
      tileMapType: tileOptions.tileMapType ?? 'base',
      visible: tileOptions.visible ?? false,
    };

    // Model load logic is moved to the effect above so that it's handled centrally.
    // And the logic is in the effect because setSceneProjection requires model load/unload.
    this.addItem(optionsWithDefaults);
  }

  /**
   * Sets the viewer position to the given coordinate.
   * @param coordinate The coordinate to set the viewer position to.
   * @param height The height to set the viewer position to. If 'current', the current height is used.
   */
  public async setViewerPosition(
    coordinate: [Longitude, Latitude],
    height?: number | 'current'
  ): Promise<void> {
    const position = await this.coordinateToPosition(coordinate);
    let cameraHeight;
    if (height === 'current') {
      cameraHeight = this.viewer.camera.position.z;
    } else if (height) {
      cameraHeight = height;
    } else {
      cameraHeight = this.DEFAULT_CAMERA_HEIGHT;
    }

    this.viewer.camera.position = new Vector3(
      position.x,
      position.y,
      cameraHeight
    );
  }

  private get tileMapPlugin(): TileMapPlugin {
    const plugin = this.viewer.plugins.get(TileMapPlugin);
    if (!plugin) {
      throw new Error('TileMapPlugin is not available on the viewer');
    }
    return plugin;
  }

  /**
   * Converts a coordinate to a position in the viewer using the defined projection.
   * @param coordinate The coordinate to convert.
   * @returns The position in the viewer.
   */
  public async coordinateToPosition(
    coordinate: [Longitude, Latitude]
  ): Promise<Vector2> {
    await this.sceneProjectionConfiguredPromise;
    if (this.sceneProjection?.tglDef) {
      await this.tglInitializedPromise;
      const convertedCoordinate = lonLatToWebMercatorMeters({
        lat: coordinate[1],
        lon: coordinate[0],
      });
      coordinate = [convertedCoordinate.x, convertedCoordinate.y];
    } else {
      getLogger().debug(
        'No tglDef, proceeding without waiting',
        this.sceneProjection
      );
    }

    const position = this.tileMapPlugin.cartographicToPosition(
      new Vector2(coordinate[0], coordinate[1])
    );
    return new Vector2(position.x, position.y);
  }

  /**
   * Converts a position to a coordinate in the viewer using the defined projection.
   * @param position The position to convert.
   * @returns The coordinate in the viewer.
   */
  public async positionToCoordinate(position: Vector2): Promise<Vector2> {
    // Wait for the first model to be loaded
    await this.modelLoadedPromise;
    await this.sceneProjectionConfiguredPromise;

    const modelId = this.selectedItemId();
    if (modelId == null) {
      throw new Error('Model id is not selected!');
    }
    const model = this.viewer.getModel(modelId, TileMapModel);
    if (!model) {
      throw new Error(modelId + ' is not loaded!');
    }

    const coordinates = this.tileMapPlugin.positionToCartographic(position);
    return new Vector2(coordinates.x, coordinates.y);
  }

  public async cleanup(): Promise<void> {
    this.setSelectedItemId(null);
    const unloadPromises: Promise<void>[] = this.items().map((model) => {
      if (this.viewer.getModel(model.modelId)) {
        return this.viewer.unload(model.modelId, { fitToView: false });
      }
      return Promise.resolve();
    });
    await Promise.all(unloadPromises);
    this.setItems([]);
  }
}
